import React from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvoicesList = ({ invoices, onDownload, downloadingId, loading }) => {
  const getStatusColor = (status) => {
    const colors = {
      completed: 'text-green-600 bg-green-50',
      pending: 'text-yellow-600 bg-yellow-50',
      failed: 'text-red-600 bg-red-50'
    };
    return colors?.[status] || colors?.pending;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Loading invoices...</p>
      </div>
    );
  }

  if (!invoices || invoices?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">No Invoices Available</h3>
        <p className="text-muted-foreground">You don't have any invoices yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-bold text-foreground mb-1">Invoices</h2>
        <p className="text-sm text-muted-foreground">{invoices?.length} invoice{invoices?.length !== 1 ? 's' : ''} available</p>
      </div>

      {/* Invoices Grid */}
      <div className="p-6 space-y-4">
        {invoices?.map((invoice) => (
          <div
            key={invoice?.id}
            className="border border-border rounded-lg p-5 hover:shadow-md transition-shadow bg-background"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-foreground text-lg">
                      Invoice #{invoice?.invoice_number}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice?.payment_status)}`}>
                      {invoice?.payment_status?.charAt(0)?.toUpperCase() + invoice?.payment_status?.slice(1)}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Calendar" size={16} />
                      <span>Date: {format(new Date(invoice?.billing_date), 'MMM dd, yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="DollarSign" size={16} />
                      <span>Amount: ${parseFloat(invoice?.total_amount || 0)?.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Clock" size={16} />
                      <span>Due: {format(new Date(invoice?.due_date), 'MMM dd, yyyy')}</span>
                    </div>
                  </div>
                  {invoice?.payment_date && (
                    <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                      <Icon name="CheckCircle" size={16} className="text-green-600" />
                      <span>Paid on {format(new Date(invoice?.payment_date), 'MMM dd, yyyy')}</span>
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownload(invoice?.id)}
                disabled={downloadingId === invoice?.id}
              >
                {downloadingId === invoice?.id ? (
                  <>
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Icon name="Download" size={16} />
                    Download PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InvoicesList;